import { PageController } from '@client/components/_core/layout/PageController';
import {
    EmptyValue,
    InitialsAvatar,
    RecordCard,
    RecordTableToolbar,
    recordTablePagination,
} from '@client/components/_ui/display/RecordPrimitives';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input } from 'antd';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useClientRecord } from './client-record-data';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/drivers')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <DriversTab />
        </PageController>
    );
}

type DriverQueryParams = RouterInputs['fleetClientDb']['listFleetDrivers'];
type DriverRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetDrivers']>['rows'][number];
const columnHelper = createColumnHelper<DriverRow, NonNullable<DriverQueryParams['orderBy']>>();

function DriversTab() {
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const { formatDate } = useFormatter();
    const [params, setParams] = useState<DriverQueryParams>({
        limit: 20,
        orderBy: { name: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');

    const isClient = client ? !isConsolidatorRecord(client) : false;
    const driversQuery = trpc.fleetClientDb.listFleetDrivers.useQuery(
        isClient ? { ...params, where: { ...params.where, clientId: id } } : skipToken,
    );
    const hasActiveFilters = !!params.where?.name;

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: driversQuery.data?.rows || [],
        total: driversQuery.data?.total || 0,
        loading: driversQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No drivers"
                noRecordsDescription="Drivers appear here once a vehicle on this client names them as its primary driver."
                noMatchesTitle="No drivers match"
                noMatchesDescription="Try another name or clear the search."
            />
        ),
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Driver',
                sortable: 'name',
                cell: ({ row }) => (
                    <div className="flex items-center gap-2.5">
                        <InitialsAvatar name={row.name} size="small" />
                        <span className="font-semibold">{row.name}</span>
                    </div>
                ),
            }),
            columnHelper.accessor('vehiclesCount', {
                id: 'vehiclesCount',
                header: 'Vehicles',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.vehiclesCount}</span>,
            }),
            columnHelper.accessor('mobile', {
                id: 'mobile',
                header: 'Mobile',
                width: 150,
                cell: ({ row }) =>
                    row.mobile ? (
                        <span className="whitespace-nowrap tabular-nums">{row.mobile}</span>
                    ) : (
                        <EmptyValue>—</EmptyValue>
                    ),
            }),
            columnHelper.accessor('email', {
                id: 'email',
                header: 'Email',
                cell: ({ row }) =>
                    row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('licenceNo', {
                id: 'licence',
                header: 'Licence',
                width: 220,
                cell: ({ row }) => {
                    if (!row.licenceNo) {
                        return <EmptyValue>—</EmptyValue>;
                    }
                    const detail = [
                        row.licenceState,
                        row.licenceExpiry ? `expires ${formatDate(row.licenceExpiry)}` : null,
                    ]
                        .filter(Boolean)
                        .join(', ');
                    return (
                        <span className="flex items-baseline gap-2 whitespace-nowrap">
                            <span className="tabular-nums">{row.licenceNo}</span>
                            {detail ? <span className="text-subtle-foreground text-xs">{detail}</span> : null}
                        </span>
                    );
                },
            }),
        ],
    });

    if (client && !isClient) {
        return null;
    }

    return (
        <RecordCard
            padded={false}
            footer={
                <>
                    <Info className="size-3.5 shrink-0" />
                    Drivers are derived from the client's vehicles; contact and licence details come with the carparc
                    feed when the source supplies them.
                </>
            }
        >
            <RecordTableToolbar>
                <Input.Search
                    placeholder="Search by driver name"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) =>
                        setParams((prev) => ({
                            ...prev,
                            offset: 0,
                            where: {
                                ...prev.where,
                                name: value.trim() ? { contains: value.trim(), mode: 'insensitive' } : undefined,
                            },
                        }))
                    }
                />
            </RecordTableToolbar>
            {driversQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={driversQuery.error} onRetry={() => void driversQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable {...tableProps} />
            )}
        </RecordCard>
    );
}
